// =============================================================================
// AGC: DROID FARM - NDVI Overlay Component
// AGC: Displays NDVI satellite imagery overlay on Leaflet maps
// AGC: Shows vegetation health visualization from Sentinel-2 data
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { ImageOverlay, useMap } from 'react-leaflet'
import { LatLngBounds } from 'leaflet'
import { Satellite, RefreshCw, AlertCircle, Eye, EyeOff, Layers } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { getBackendUrl } from '../../utils/backendUrl'

// AGC: Backend API base URL — centralized via getBackendUrl()
const API_BASE_URL = getBackendUrl()

interface NDVIOverlayProps {
  // AGC: Whether the overlay is visible
  visible?: boolean
  // AGC: Opacity of the overlay (0-1)
  opacity?: number
  // AGC: Time window in days for satellite data
  timeWindowDays?: number
  // AGC: Callback when visibility changes
  onVisibilityChange?: (visible: boolean) => void
  // AGC: Callback when opacity changes
  onOpacityChange?: (opacity: number) => void
  // AGC: Callback on error
  onError?: (error: string) => void
}

// AGC: NDVI Legend component
const NDVILegend: React.FC<{ visible: boolean }> = ({ visible }) => {
  const { t } = useTranslation()
  
  if (!visible) return null

  const legendItems = [
    { color: '#8b5a2b', label: t('fields.ndvi.bareSoil'), range: '< 0' },
    { color: '#c8a064', label: t('fields.ndvi.sparse'), range: '0 - 0.2' },
    { color: '#d4d464', label: t('fields.ndvi.moderate'), range: '0.2 - 0.4' },
    { color: '#64c864', label: t('fields.ndvi.healthy'), range: '0.4 - 0.6' },
    { color: '#32a032', label: t('fields.ndvi.veryHealthy'), range: '0.6 - 0.8' },
    { color: '#1e6e1e', label: t('fields.ndvi.dense'), range: '> 0.8' },
  ]

  return (
    <div className="absolute bottom-4 left-4 bg-gray-900/90 rounded-lg p-3 text-xs z-[1000]">
      <div className="font-medium text-white mb-2 flex items-center gap-2">
        <Layers className="w-4 h-4" />
        {t('fields.ndvi.legend')}
      </div>
      <div className="space-y-1">
        {legendItems.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <div 
              className="w-4 h-3 rounded"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-gray-300">{item.label}</span>
            <span className="text-gray-500 ml-auto">{item.range}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// AGC: NDVI Controls component
interface NDVIControlsProps {
  visible: boolean
  opacity: number
  loading: boolean
  error: string | null
  onToggle: () => void
  onOpacityChange: (opacity: number) => void
  onRefresh: () => void
}

const NDVIControls: React.FC<NDVIControlsProps> = ({
  visible,
  opacity,
  loading,
  error,
  onToggle,
  onOpacityChange,
  onRefresh,
}) => {
  const { t } = useTranslation()

  return (
    <div className="absolute top-4 right-4 bg-gray-900/90 rounded-lg p-3 z-[1000] space-y-3">
      {/* AGC: Toggle button */}
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          visible
            ? 'bg-green-600 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
      >
        {loading ? (
          <RefreshCw className="w-4 h-4 animate-spin" />
        ) : visible ? (
          <Eye className="w-4 h-4" />
        ) : (
          <EyeOff className="w-4 h-4" />
        )}
        <span>{t('fields.ndvi.overlay')}</span>
      </button>

      {/* AGC: Opacity slider (only when visible) */}
      {visible && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-400">
            <span>{t('fields.ndvi.opacity')}</span>
            <span>{Math.round(opacity * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={opacity * 100}
            onChange={(e) => onOpacityChange(parseInt(e.target.value) / 100)}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
          />
        </div>
      )}

      {/* AGC: Refresh button */}
      {visible && (
        <button
          onClick={onRefresh}
          disabled={loading}
          className="flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-sm bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('fields.ndvi.refresh')}
        </button>
      )}

      {/* AGC: Error message */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{error}</span>
        </div>
      )}
    </div>
  )
}

// AGC: Main NDVI Overlay component with map integration
export const NDVIOverlayLayer: React.FC<NDVIOverlayProps> = ({
  visible: initialVisible = false,
  opacity: initialOpacity = 0.6,
  timeWindowDays = 30,
  onVisibilityChange,
  onOpacityChange,
  onError,
}) => {
  const { t } = useTranslation()
  const map = useMap()
  
  const [visible, setVisible] = useState(initialVisible)
  const [opacity, setOpacity] = useState(initialOpacity)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageBounds, setImageBounds] = useState<LatLngBounds | null>(null)
  const [lastFetchBounds, setLastFetchBounds] = useState<string | null>(null)

  // AGC: Fetch NDVI overlay for current map bounds
  const fetchNdviOverlay = useCallback(async () => {
    if (!map) return

    const bounds = map.getBounds()
    const size = map.getSize()

    // AGC: Generate bounds key to prevent duplicate fetches
    const boundsKey = `${bounds.getSouthWest().lat.toFixed(4)},${bounds.getSouthWest().lng.toFixed(4)},${bounds.getNorthEast().lat.toFixed(4)},${bounds.getNorthEast().lng.toFixed(4)}`
    
    if (boundsKey === lastFetchBounds && imageUrl) {
      return // AGC: Already fetched for these bounds
    }

    setLoading(true)
    setError(null)

    try {
      // AGC: Build overlay URL
      const params = new URLSearchParams({
        minLon: bounds.getSouthWest().lng.toString(),
        minLat: bounds.getSouthWest().lat.toString(),
        maxLon: bounds.getNorthEast().lng.toString(),
        maxLat: bounds.getNorthEast().lat.toString(),
        width: Math.min(1024, size.x).toString(),
        height: Math.min(1024, size.y).toString(),
        days: timeWindowDays.toString(),
      })

      const response = await fetch(`${API_BASE_URL}/api/v1/boundaries/ndvi/overlay?${params}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: { message: 'Unknown error' } }))
        throw new Error(errorData.error?.message || `HTTP ${response.status}`)
      }

      // AGC: Convert response to blob URL
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      // AGC: Revoke old URL to prevent memory leak
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl)
      }

      setImageUrl(url)
      setImageBounds(bounds)
      setLastFetchBounds(boundsKey)
      setError(null)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch NDVI overlay'
      setError(errorMessage)
      onError?.(errorMessage)
      console.error('[NDVIOverlay] AGC: Fetch error:', err)
    } finally {
      setLoading(false)
    }
  }, [map, timeWindowDays, lastFetchBounds, imageUrl, onError])

  // AGC: Handle visibility toggle
  const handleToggle = useCallback(() => {
    const newVisible = !visible
    setVisible(newVisible)
    onVisibilityChange?.(newVisible)

    if (newVisible && !imageUrl) {
      fetchNdviOverlay()
    }
  }, [visible, imageUrl, onVisibilityChange, fetchNdviOverlay])

  // AGC: Handle opacity change
  const handleOpacityChange = useCallback((newOpacity: number) => {
    setOpacity(newOpacity)
    onOpacityChange?.(newOpacity)
  }, [onOpacityChange])

  // AGC: Handle refresh
  const handleRefresh = useCallback(() => {
    setLastFetchBounds(null) // AGC: Force re-fetch
    fetchNdviOverlay()
  }, [fetchNdviOverlay])

  // AGC: Fetch on initial load if visible
  useEffect(() => {
    if (visible && !imageUrl && !loading) {
      fetchNdviOverlay()
    }
  }, [visible, imageUrl, loading, fetchNdviOverlay])

  // AGC: Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [imageUrl])

  return (
    <>
      {/* AGC: Controls */}
      <NDVIControls
        visible={visible}
        opacity={opacity}
        loading={loading}
        error={error}
        onToggle={handleToggle}
        onOpacityChange={handleOpacityChange}
        onRefresh={handleRefresh}
      />

      {/* AGC: Legend */}
      <NDVILegend visible={visible && !!imageUrl} />

      {/* AGC: Image overlay */}
      {visible && imageUrl && imageBounds && (
        <ImageOverlay
          url={imageUrl}
          bounds={imageBounds}
          opacity={opacity}
          zIndex={100}
        />
      )}
    </>
  )
}

// AGC: Standalone NDVI toggle button for use outside map
interface NDVIToggleButtonProps {
  active: boolean
  loading?: boolean
  onClick: () => void
  className?: string
}

export const NDVIToggleButton: React.FC<NDVIToggleButtonProps> = ({
  active,
  loading = false,
  onClick,
  className = '',
}) => {
  const { t } = useTranslation()

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        active
          ? 'bg-green-600 text-white'
          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
      } ${className}`}
    >
      {loading ? (
        <RefreshCw className="w-4 h-4 animate-spin" />
      ) : (
        <Satellite className="w-4 h-4" />
      )}
      <span>{t('fields.ndvi.overlay')}</span>
    </button>
  )
}

export default NDVIOverlayLayer

